<template>
  <div class="hub">
    <div class="hub-inner">
      <!-- ---------------------------------------------------------------
           Hero
      ---------------------------------------------------------------- -->
      <header class="hub-hero">
        <h1>
          Electric bike insurance: e-bike cover including the motor and battery
        </h1>
        <PageByline :reviewed-date="reviewedDate" :author-id="authorId" />

        <p class="hub-link hub-crumb-link">
          Part of our guide to
          <nuxt-link no-prefetch to="/bike-insurance"
            >how bike insurance works</nuxt-link
          >.
        </p>

        <p>
          Pedalsure e-bike insurance covers road-legal electric bikes worth up
          to £15,000 against theft, accidental damage and malicious damage, at
          home and away. The motor, battery and display are covered as part of
          the bike, and claims are settled new for old.
          <template v-if="fromPrice"
            >Cover starts from {{ fromPrice }} a month, with up to five e-bikes
            and bikes on one policy.</template
          >
        </p>

        <div class="hub-cta">
          <a
            href="/quote?bike=electric&src=lp-electric"
            class="button ps-button call-to-action"
            @click="openQuickQuote($event, 'lp-electric')"
          >
            Get a quote
          </a>
        </div>

        <p class="hub-aside">
          Only need theft cover?
          <nuxt-link no-prefetch to="/what-we-cover#cover-types"
            >Theft Only</nuxt-link
          >
          is also available.
        </p>
      </header>

      <!-- ---------------------------------------------------------------
           What's covered
      ---------------------------------------------------------------- -->
      <section class="hub-section">
        <h2>What's covered</h2>
        <div class="ps-table-wrap">
          <table class="ps-table">
            <thead>
              <tr>
                <th scope="col">Cover</th>
                <th scope="col">Details</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in coverRows" :key="row.label">
                <th scope="row">{{ row.label }}</th>
                <td data-label="Details">{{ row.detail }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Not covered: mechanical or electrical breakdown, wear and tear,
          battery deterioration and cosmetic damage.
        </p>
      </section>

      <!-- ---------------------------------------------------------------
           Is my e-bike eligible?
      ---------------------------------------------------------------- -->
      <section class="hub-section">
        <h2>Is my e-bike eligible?</h2>
        <p>
          Your e-bike must be legal on UK roads as an
          <strong>electrically assisted pedal cycle (EAPC)</strong>: broadly, a
          motor of no more than 250 watts that stops assisting above 15.5 mph.
          See <a :href="govUkEbikeRules">gov.uk electric bike rules</a>.
        </p>
        <p>
          We don't cover an e-bike that isn't an EAPC or is treated as a motor
          vehicle, ePeds, iPeds or moped-style bikes. Twist-and-go e-bikes are
          covered only if the throttle works in walk mode only.
        </p>
      </section>

      <!-- ---------------------------------------------------------------
           Optional add-ons
      ---------------------------------------------------------------- -->
      <section class="hub-section">
        <h2>Optional add-ons</h2>
        <div class="ps-table-wrap">
          <table class="ps-table">
            <thead>
              <tr>
                <th scope="col">Add-on</th>
                <th scope="col">Details</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in addOnRows" :key="row.label">
                <th scope="row">{{ row.label }}</th>
                <td data-label="Details">{{ row.detail }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>*Available with Theft and Accidental Damage only.</p>
      </section>

      <!-- ---------------------------------------------------------------
           Keeping your e-bike covered against theft
      ---------------------------------------------------------------- -->
      <section class="hub-section">
        <h2>Keeping your e-bike covered against theft</h2>
        <ul class="hub-list">
          <li>
            <strong>Lock:</strong> Sold Secure Silver, Gold or Diamond for
            e-bikes up to £1,000. Sold Secure Gold or Diamond above £1,000 (most
            e-bikes). A Thatcham-approved motorcycle lock works at any value.
          </li>
          <li>
            <strong>Through the frame</strong> to an immovable object, for no
            more than 12 hours, or 24 hours at work or in a station cycle rack.
          </li>
          <li>
            <strong>Battery:</strong> if you leave the battery on the bike, lock
            it with its built-in lock and take the key with you.
          </li>
          <li>
            <strong>Photos</strong> within 30 days of buying, and
            <strong>tell us about a claim within 30 days</strong>.
          </li>
        </ul>
        <p class="hub-link">
          <nuxt-link no-prefetch to="/blog/how-to-lock-your-e-bike">
            How to lock your e-bike →
          </nuxt-link>
          ·
          <nuxt-link no-prefetch to="/what-we-cover#security">
            Full security rules →
          </nuxt-link>
        </p>
      </section>

      <!-- ---------------------------------------------------------------
           FAQs
      ---------------------------------------------------------------- -->
      <section class="hub-section">
        <h2>E-bike insurance FAQs</h2>
        <div class="ps-faq">
          <details v-for="faq in faqs" :key="faq.question" class="ps-faq-item">
            <summary>{{ faq.question }}</summary>
            <div class="ps-faq-answer">
              {{ faq.answer }}
              <nuxt-link v-if="faq.link" no-prefetch :to="faq.link.to">{{
                faq.link.label
              }}</nuxt-link>
            </div>
          </details>
        </div>
      </section>

      <!-- ---------------------------------------------------------------
           Closing CTA
      ---------------------------------------------------------------- -->
      <section class="hub-closing">
        <p>See your price for your e-bike.</p>
        <div class="hub-cta">
          <a
            href="/quote?bike=electric&src=lp-electric-footer"
            class="button ps-button call-to-action"
            @click="openQuickQuote($event, 'lp-electric-footer')"
          >
            Get a quote
          </a>
        </div>
      </section>

      <!-- ---------------------------------------------------------------
           Learn more about e-bikes — deliberately below the closing CTA,
           so it doesn't compete with the commercial intent above it.
      ---------------------------------------------------------------- -->
      <section class="hub-section">
        <h2>Learn more about e-bikes</h2>
        <ul class="hub-list">
          <li v-for="link in learnMoreLinks" :key="link.to">
            <nuxt-link no-prefetch :to="link.to">{{ link.label }}</nuxt-link>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import PageByline from '@/components/common/PageByline'
import { getCanonicalUrl } from '@/helpers/seo'
import { jsonLdScript } from '@/helpers/blog-schema'
import { getAuthor } from '@/helpers/authors'
import { fetchEntryPrice } from '@/helpers/estimator'
import { hubPageLastmod } from '@/helpers/hub-pages'
import { buildSocialMeta } from '@/helpers/social-meta'

const CANONICAL_PATH = '/electric-bike-insurance'
const OG_IMAGE_PATH = '/images/og/electric-bike-insurance-1200x630.png'
const GOV_UK_EBIKE_RULES = 'https://www.gov.uk/electric-bike-rules'
const SPOKE_POST_PATH = '/blog/do-you-need-insurance-for-an-electric-bike'

// The byline date and the sitemap's <lastmod> are the same value — see
// helpers/hub-pages.js. Bump it there, not here.
const LAST_REVIEWED = hubPageLastmod(CANONICAL_PATH)
const AUTHOR_ID = 'underwriting-lead'

// This page ranks #1 for "ebike insurance uk": the URL, title, H1, H2 order
// and the 12 FAQs stay put. Copy is the approved 11/26 SEO wording, checked
// against Policy Document 11/26 and IPID 11/26 — don't reword it here.
//
// One list, rendered on the page and quoted by the FAQPage node below. The
// schema answer is `answer` alone: a trailing `link` is shown on the page but
// kept out of the JSON-LD. The price is live (see fetchEntryPrice); when the
// engine can't supply it, the sentence that states it is dropped.
function buildFaqs(fromPrice) {
  return [
    {
      question: 'Do I need insurance for an electric bike in the UK?',
      answer:
        "No. Road-legal e-bikes (EAPCs) don't need insurance by law. Because e-bikes are expensive to replace, many riders insure them against theft and damage.",
      link: { to: SPOKE_POST_PATH, label: 'Read more →' },
    },
    {
      question: 'Are the motor and battery covered?',
      answer:
        "Yes, as part of the bike. A battery, charger or removable display stolen on its own is covered only if the bike was stolen at the same time, or the battery was locked to the bike with its built-in lock and there's visible evidence it was forced out.",
    },
    {
      question: 'Is a modified e-bike covered?',
      answer:
        "Only if it's still a road-legal EAPC. There's no cover for an e-bike that isn't an EAPC or is treated as a motor vehicle, for example one changed to assist above 15.5 mph or fitted with a motor over 250 watts.",
    },
    {
      question: 'Is my cycle to work e-bike covered?',
      answer:
        "Yes. You can insure an e-bike you hold under a cycle to work, hire or finance agreement if you're legally responsible for it. If you claim, a copy of the agreement counts as your evidence of ownership.",
    },
    {
      question: 'Can my family ride my insured e-bike?',
      answer:
        'Yes. Your e-bike is covered when ridden by your parent, spouse, partner, son, daughter, brother or sister who lives permanently with you. Personal Accident and Personal Liability cover only you.',
    },
    {
      question: 'Am I covered if I injure someone?',
      answer:
        "Only if you add Personal Liability. It covers compensation you're legally liable for if you injure someone or damage their property while riding, up to the limit on your Policy Schedule. It doesn't pay if you have liability cover elsewhere.",
    },
    {
      question: 'Do I need extra cover for a sportive?',
      answer:
        "No. Sportives and charity rides where no winner is selected don't need competition cover. Races and triathlons where a winner is selected do.",
    },
    {
      question: 'Are e-MTBs covered?',
      answer:
        "Yes, if they're road-legal EAPCs worth up to £15,000. The same lock and storage rules apply. Stunts and stunt equipment, such as ramps, aren't covered.",
    },
    {
      question: 'Am I covered abroad?',
      answer:
        "You're covered in the UK, the Channel Islands and the Isle of Man. With Theft and Accidental Damage, you can add worldwide cover for 30 or 60 days a year.",
    },
    {
      question: 'Can I use my e-bike for delivery or courier work?',
      answer:
        "No. There's no cover for business use, including food delivery and courier work. Commuting to and from work is covered.",
    },
    {
      question: 'How is a claim settled?',
      answer:
        "New for old. We repair or replace your e-bike with the same make and model (or equivalent if that's not available), with no deduction for wear and tear, up to your sum insured, less the excess. If you're underinsured, we reduce the payment in proportion.",
    },
    {
      question: 'How much does e-bike insurance cost?',
      answer: `${
        fromPrice ? `From ${fromPrice} a month. ` : ''
      }Your price depends on your e-bike's value and the cover you choose. We won't change it part-way through the year because you've claimed.`,
    },
  ]
}

export default {
  name: 'ElectricBikeInsurance',
  components: { PageByline },
  // 'free' renders the sitewide header, footer and quote modal without the
  // Ghost-backed page fetch 'static-page' does — this page's copy is its own,
  // and nothing here should depend on a CMS request succeeding.
  layout: 'free',
  async asyncData({ $axios }) {
    return { entryPrice: await fetchEntryPrice($axios) }
  },
  data() {
    return {
      entryPrice: null,
      reviewedDate: LAST_REVIEWED,
      authorId: AUTHOR_ID,
      govUkEbikeRules: GOV_UK_EBIKE_RULES,
      coverRows: [
        {
          label: 'Theft',
          detail: 'At home and away, when the lock and storage rules are met',
        },
        {
          label: 'Accidental and malicious damage',
          detail: 'Crash damage and vandalism, including the motor and battery',
        },
        {
          label: 'New-for-old replacement',
          detail:
            "Same make and model, or equivalent if that's not available. No deduction for wear and tear.",
        },
        {
          label: 'Replacement bike hire',
          detail:
            'Up to £500 per policy year while an approved claim is settled',
        },
        {
          label: 'Up to five bikes',
          detail: 'Up to £15,000 each and £30,000 in total',
        },
        {
          label: 'Family riders',
          detail:
            'Covered when ridden by close family (parent, partner, child, brother or sister) who live permanently with you',
        },
        {
          label: 'Price changes only at renewal',
          detail: "We won't change your price mid-year because you've claimed",
        },
      ],
      addOnRows: [
        {
          label: 'Personal Accident (Bronze, Silver or Gold)',
          detail:
            'Up to £10,000, £25,000 or £50,000 for serious injury, plus physiotherapy, dental and hospital benefits',
        },
        {
          label: 'Loss of earnings',
          detail:
            'Weekly benefit up to the amount on your Policy Schedule (max 75% of your average weekly wage), for up to 52 weeks from the accident. Nothing for the first 14 days.',
        },
        {
          label: 'Personal Liability',
          detail:
            "If you injure someone or damage their property, up to the limit on your Policy Schedule. Doesn't pay if you have liability cover elsewhere.",
        },
        {
          label: 'Accessories and personal possessions*',
          detail:
            '£250, £500 or £1,000 for kit, lights, panniers and clothing, if stolen or damaged with your e-bike',
        },
        {
          label: 'Worldwide cover*',
          detail: '30 or 60 days a year',
        },
        {
          label: 'Competition cover*',
          detail: 'Races and triathlons where a winner is selected',
        },
      ],
      learnMoreLinks: [
        {
          to: SPOKE_POST_PATH,
          label: 'Do you need insurance for an electric bike?',
        },
        {
          to: '/blog/how-to-lock-your-e-bike',
          label: 'How to lock your e-bike',
        },
        {
          to: '/blog/beginners-guide-to-e-bikes',
          label: "Beginner's guide to e-bikes",
        },
        {
          to: '/blog/does-home-insurance-cover-my-bike',
          label: 'Does home insurance cover my bike?',
        },
      ],
    }
  },
  head() {
    const title = 'E-Bike Insurance UK | Electric Bike Cover | Pedalsure'
    const description =
      'E-bike insurance for road-legal electric bikes worth up to £15,000. Theft and accidental damage cover, with the motor and battery included.'
    const canonicalUrl = getCanonicalUrl(CANONICAL_PATH)
    const author = getAuthor(AUTHOR_ID)

    return {
      title,
      meta: [
        { hid: 'description', name: 'description', content: description },
        { hid: 'author', name: 'author', content: author ? author.name : '' },
        { hid: 'robots', name: 'robots', content: 'index,follow' },
        ...buildSocialMeta({
          title,
          description,
          path: CANONICAL_PATH,
          image: OG_IMAGE_PATH,
          imageAlt: 'Electric bike insurance from Pedalsure',
          type: 'website',
        }).meta,
      ],
      link: [{ hid: 'canonical', rel: 'canonical', href: canonicalUrl }],
      script: [jsonLdScript('ld-electric-bike-insurance', this.structuredData)],
    }
  },
  computed: {
    fromPrice() {
      return this.entryPrice
        ? this.$options.filters.toCurrency(this.entryPrice.monthly)
        : null
    },
    faqs() {
      return buildFaqs(this.fromPrice)
    },
    structuredData() {
      const canonicalUrl = getCanonicalUrl(CANONICAL_PATH)

      return {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'FAQPage',
            '@id': `${canonicalUrl}#faq`,
            mainEntity: this.faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: getCanonicalUrl('/'),
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Bike insurance',
                item: getCanonicalUrl('/bike-insurance'),
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: 'Electric bike insurance',
                item: canonicalUrl,
              },
            ],
          },
        ],
      }
    },
  },
  methods: {
    ...mapActions('quote', {
      resetQuote: 'resetQuote',
    }),
    // The CTA is a real link — what a crawler follows, a middle-click opens
    // and a screen reader announces — and the quick-quote panel is the fast
    // path for a plain left click. A modified click is the visitor asking for
    // a new tab, so it's left alone. Same src as the href, so a quote started
    // either way is attributed identically.
    openQuickQuote(event, src) {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) {
        return
      }
      event.preventDefault()
      this.resetQuote()
      this.$modal.show('quick-quote', { bikeType: 'electric', src })
    },
  },
}
</script>

<style lang="scss" scoped>
// Mobile first, one reading column. Nothing here restyles a table or the FAQ
// rows — those are the sitewide .ps-table / .ps-faq patterns in
// assets/pedalsure-standard.css, so this page inherits them like the rest of
// the hub series.
.hub {
  padding: 24px 16px 56px;
  @include tablet {
    padding: 40px 24px 80px;
  }
}

.hub-inner {
  max-width: 820px;
  margin: 0 auto;
}

.hub-hero {
  h1 {
    font-size: clamp(30px, 7vw, $ps-text-h1);
    line-height: 1.08;
    font-weight: 700;
    letter-spacing: -0.025em;
    color: $ps-ink;
  }
}

.hub-crumb-link {
  margin-top: 10px !important;
}

.hub-section,
.hub-hero,
.hub-closing {
  p {
    margin-top: 14px;
    font-size: $ps-text-small;
    line-height: 1.6;
    color: $ps-body-color;
    @include tablet {
      font-size: $ps-text-body;
    }
  }

  // The one-line Theft Only pointer: deliberately quieter than the body copy,
  // never a button or a card.
  p.hub-aside {
    font-size: $ps-text-label;
    font-style: italic;
  }

  strong {
    color: $ps-ink;
  }

  // Body links only. A CTA here is an <a> carrying the sitewide button
  // classes, and this rule out-specifies `.call-to-action` (0,1,1 vs 0,1,0) —
  // which is how the green buttons ended up with indigo text on indigo-free
  // green. Excluding .button leaves the button system to colour its own
  // buttons.
  a:not(.button) {
    color: $ps-primary;
    text-decoration: underline;
  }
}

.hub-section {
  margin-top: 40px;
  @include tablet {
    margin-top: 56px;
  }

  h2 {
    font-size: $ps-text-h3;
    font-weight: 700;
    line-height: 1.2;
    color: $ps-ink;
    @include tablet {
      font-size: $ps-text-h2;
    }
  }

  .ps-table-wrap {
    margin-top: 14px;
  }
}

.hub-list {
  margin-top: 14px;

  li {
    position: relative;
    padding-left: 22px;
    font-size: $ps-text-small;
    line-height: 1.6;
    color: $ps-body-color;
    @include tablet {
      font-size: $ps-text-body;
    }
  }

  li + li {
    margin-top: 10px;
  }

  li::before {
    content: '';
    position: absolute;
    left: 2px;
    top: 0.62em;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: $ps-primary;
  }
}

.hub-steps {
  margin: 14px 0 0;
  padding: 0;
  list-style: none;
  counter-reset: hub-step;

  li {
    position: relative;
    padding-left: 34px;
    font-size: $ps-text-small;
    line-height: 1.6;
    color: $ps-body-color;
    @include tablet {
      font-size: $ps-text-body;
    }
  }

  li + li {
    margin-top: 12px;
  }

  li::before {
    counter-increment: hub-step;
    content: counter(hub-step);
    position: absolute;
    left: 0;
    top: 0.15em;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: $ps-primary-50;
    color: $ps-primary;
    font-size: $ps-text-label;
    font-weight: 700;
  }
}

// The CTA rows. Buttons are the sitewide .button/.ps-button classes; only the
// stacking is page-level, because these labels are full sentences and must be
// allowed to wrap rather than overflow a phone.
.hub-cta {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;

  .button {
    height: auto;
    min-height: $ps-btn-h-cta;
    padding: 14px 24px;
    white-space: normal;
    text-align: center;
    text-decoration: none;
    line-height: 1.35;
  }

  @include tablet {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: stretch;
  }
}

.hub-link {
  a {
    font-weight: 600;
  }
}

.hub-closing {
  margin-top: 48px;
  padding: 24px 20px;
  border-radius: $ps-radius-lg;
  background: $ps-surface;

  p {
    margin-top: 0;
    color: $ps-ink;
    font-weight: 600;
  }

  @include tablet {
    padding: 32px;
  }
}

.is-vhidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
</style>
